/**
 * KundenKarte — Leaflet-Karte mit Pins fuer alle Beratungskunden, die
 * geokodierte Koordinaten haben. Kunden ohne lat/lng werden stillschweigend
 * uebersprungen (zeigen wir spaeter ggf. als kleine Stat-Zeile darunter an).
 *
 * Properties:
 *   kunden            — Array aus /api/kunden (mit `lat`, `lng`, `firstName`, ...)
 *   onOpenKunde       — Callback (kundeId) => void, springt in die Akte
 *   touren            — Aktive Touren fuer das "Zu Tour hinzufuegen"-Popup
 *   onAddZuTour       — Callback (kundeId, tourId) => void
 *   highlightKundeId  — Pin dieses Kunden wird orange + groesser dargestellt
 *                       (z.B. der aktuell offene Kunde in der Akte).
 *
 * Wichtig:
 *   - OpenStreetMap-Tiles (kein Google), siehe ATTRIBUTION unten.
 *   - Marker-Cluster bei vielen Pins, sonst sieht man Stadtballungen nicht.
 *   - Hoehe ist im Container fest auf 480px gesetzt — Leaflet braucht eine
 *     definierte Hoehe, sonst rendert die Karte 0px hoch und ist unsichtbar.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api, ApiError } from '../api';

// Leaflet bringt seine Pin-Icons als statische Dateien mit, die per
// Default-Pfad geladen werden. Bei Vite-Builds funktioniert dieser Pfad
// nicht — wir mappen die Icon-URLs explizit auf die im node_modules
// liegenden Bilder, damit der Standard-Marker erscheint.
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

L.Icon.Default.mergeOptions({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
});

// Drei Pin-Varianten:
//   - highlight    -> rosa + groesser (der aktuell ausgewaehlte Kunde)
//   - plz_fallback -> grau (nur PLZ-Bereich, keine exakte Adresse)
//   - normal       -> brombeer
function pinIcon(quelle, highlight = false) {
  let farbe, size;
  if (highlight) { farbe = '#EC4899'; size = 34; }              // rosa
  else if (quelle === 'plz_fallback') { farbe = '#9CA3AF'; size = 24; }  // grau
  else { farbe = '#9D174D'; size = 24; }                        // brombeer
  const border = highlight ? '3px solid white' : '2px solid white';
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      background: ${farbe};
      border: ${border};
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      box-shadow: 0 ${highlight ? 4 : 2}px ${highlight ? 8 : 4}px rgba(0,0,0,${highlight ? 0.45 : 0.3});
    "></div>`;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size - 2],
    popupAnchor: [0, -(size - 2)],
  });
}

// Hilfs-Komponente, die die Karte automatisch auf die Pins zoomt,
// sobald sich die Daten aendern.
function FitBoundsAufKunden({ punkte }) {
  const map = useMap();
  useEffect(() => {
    if (!punkte || punkte.length === 0) return;
    const bounds = L.latLngBounds(punkte.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
  }, [punkte, map]);
  return null;
}

// Hilfs-Komponente, die einen Map-Ref nach aussen weiterreicht, damit der
// Such-Handler (ausserhalb von MapContainer) Methoden wie flyTo aufrufen kann.
function MapRefBridge({ mapRefOut }) {
  const map = useMap();
  useEffect(() => {
    mapRefOut.current = map;
  }, [map, mapRefOut]);
  return null;
}

// ==========================================================================
// NeueTourPopupForm — Inline-Anlage einer Tour direkt im Pin-Popup
// ==========================================================================
// Wichtig: hat einen EIGENEN useState fuer Name + Datum + Saving-Flag.
// Wenn der State in der Eltern-Komponente KundenKarte laege, wuerde jeder
// Tipp ein KundenKarte-Re-Render und damit ein Marker-Rebuild ausloesen —
// das Input-Element wuerde neu gemountet und der Fokus ginge nach jedem
// Buchstaben verloren. Die Sub-Komponente isoliert den State, sodass nur
// dieses kleine Form-Stueck rerendert.
// ==========================================================================
function NeueTourPopupForm({ kundeId, kundeName, onAngelegt }) {
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState('');
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10));
  const [isSaving, setIsSaving] = useState(false);

  const reset = () => { setOffen(false); setName(''); };

  async function speichern() {
    const n = (name || '').trim();
    if (!n) { alert('Bitte einen Namen fuer die Tour eingeben.'); return; }
    if (!datum) { alert('Bitte ein Datum fuer die Tour waehlen.'); return; }
    setIsSaving(true);
    try {
      const tour = await api.post('/api/touren', { name: n, datum });
      await api.post(`/api/touren/${tour.id}/kunden`, { kunde_id: kundeId, status: 'planung' });
      if (onAngelegt) onAngelegt(tour);
      reset();
      alert(`Tour „${n}" am ${datum} angelegt — ${kundeName} eingetragen.`);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      alert('Konnte Tour nicht anlegen: ' + msg);
    } finally {
      setIsSaving(false);
    }
  }

  if (!offen) {
    return (
      <button
        type="button"
        onClick={() => setOffen(true)}
        style={{
          width: '100%',
          padding: '5px 8px',
          background: 'white',
          border: '1px dashed #9D174D',
          color: '#9D174D',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 700,
          cursor: 'pointer',
        }}
        title="Neue Tour anlegen und diesen Kunden gleich eintragen"
      >
        + Neue Tour anlegen
      </button>
    );
  }

  return (
    <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '4px', padding: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Tourname (z.B. Bremen Nord)"
        style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', width: '100%', boxSizing: 'border-box' }}
        autoFocus
        disabled={isSaving}
      />
      <input
        type="date"
        value={datum}
        onChange={(e) => setDatum(e.target.value)}
        style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', width: '100%', boxSizing: 'border-box' }}
        disabled={isSaving}
      />
      <div style={{ display: 'flex', gap: '4px' }}>
        <button
          type="button"
          onClick={speichern}
          disabled={isSaving || !name.trim()}
          style={{ flex: 1, padding: '5px 8px', background: '#9D174D', color: 'white', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', opacity: isSaving || !name.trim() ? 0.5 : 1 }}
        >
          {isSaving ? '…' : 'Anlegen + Kunden eintragen'}
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={isSaving}
          style={{ padding: '5px 8px', background: 'white', color: '#666', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', cursor: 'pointer' }}
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}


export default function KundenKarte({ kunden, onOpenKunde, touren = [], onAddZuTour, onTourenReload, highlightKundeId, zentrumKunde = null, labelsAnzeigen = false }) {
  // Touren als lokalen State spiegeln — damit eine im Popup neu angelegte
  // Tour sofort in derselben Liste auftaucht, ohne dass der Parent reloaden
  // muss. Wenn der Parent doch neue Daten liefert, gewinnt der Prop.
  const [lokaleTouren, setLokaleTouren] = useState(touren);
  useEffect(() => { setLokaleTouren(touren || []); }, [touren]);

  function handleTourAngelegt(tour) {
    setLokaleTouren(prev => [tour, ...(prev || [])]);
    if (onTourenReload) onTourenReload();
  }
  const punkte = useMemo(() => {
    return (kunden || []).filter(
      k => typeof k.lat === 'number' && typeof k.lng === 'number'
    );
  }, [kunden]);

  const ohneKoords = (kunden?.length || 0) - punkte.length;

  // Wenn ein Zentrums-Kunde uebergeben ist (Umkreis-Modal), startet die Karte
  // eng auf ihm zentriert — er ist sofort in der Mitte, ohne Radius-Zwang.
  // Zoom 12 ~ Stadt/Umgebung. Sonst: Deutschland grob, danach FitBounds.
  const hatZentrum =
    zentrumKunde && typeof zentrumKunde.lat === 'number' && typeof zentrumKunde.lng === 'number';
  const center = hatZentrum ? [zentrumKunde.lat, zentrumKunde.lng] : [51.1657, 10.4515];
  const zoom = hatZentrum ? 12 : 6;

  // Den hervorgehobenen Kunden ziehen wir aus dem Cluster heraus und rendern
  // ihn separat — so bleibt er (rosa + Namensschild) immer sichtbar, auch
  // wenn drumherum Pins zu Zahlen-Clustern zusammenfallen.
  const highlightKunde = highlightKundeId
    ? punkte.find(k => k.id === highlightKundeId) || null
    : null;

  const mapRef = useRef(null);
  const markerRefs = useRef({});   // kundeId -> Leaflet-Marker fuer Popup-Open
  const [suche, setSuche] = useState('');
  const [ortLoading, setOrtLoading] = useState(false);
  const [ortFehler, setOrtFehler] = useState(null);

  // Bis zu 8 Kunden-Vorschlaege, deren Name oder PLZ/Ort zum Sucheingabe-Text passen.
  const treffer = useMemo(() => {
    const q = (suche || '').trim().toLowerCase();
    if (q.length < 2) return [];
    return punkte
      .filter(k => {
        const haystack = [
          k.firstName, k.lastName,
          (k.lastName && k.firstName) ? `${k.lastName}, ${k.firstName}` : '',
          k.zip, k.city, k.street,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 8);
  }, [punkte, suche]);

  function springeZuKunde(k) {
    if (!mapRef.current) return;
    mapRef.current.flyTo([k.lat, k.lng], 13, { duration: 0.8 });
    // Popup vom Marker oeffnen (falls Marker-Ref vorhanden)
    setTimeout(() => {
      const m = markerRefs.current[k.id];
      if (m && m.openPopup) m.openPopup();
    }, 850);
    setSuche('');
  }

  async function suchOrt() {
    const q = (suche || '').trim();
    if (q.length < 2) return;
    setOrtLoading(true);
    setOrtFehler(null);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&countrycodes=de&limit=1`;
      const r = await fetch(url, { headers: { 'Accept-Language': 'de' } });
      if (!r.ok) throw new Error('Geocoding-Server nicht erreichbar');
      const j = await r.json();
      if (!Array.isArray(j) || j.length === 0) {
        setOrtFehler(`Kein Ort fuer „${q}" gefunden.`);
        return;
      }
      const t = j[0];
      const lat = parseFloat(t.lat);
      const lng = parseFloat(t.lon);
      if (mapRef.current) {
        mapRef.current.flyTo([lat, lng], 12, { duration: 0.8 });
      }
      setSuche('');
    } catch (e) {
      console.warn('Ort-Suche fehlgeschlagen', e);
      setOrtFehler('Ort-Suche fehlgeschlagen — bitte spaeter erneut.');
    } finally {
      setOrtLoading(false);
    }
  }

  function handleSubmitSuche(e) {
    e.preventDefault();
    if (treffer.length > 0) {
      springeZuKunde(treffer[0]);
    } else {
      suchOrt();
    }
  }

  // Ein einzelner Pin inkl. Popup — als Funktion, damit wir denselben Code
  // sowohl fuer den separat gerenderten Highlight-Pin als auch fuer die Pins
  // im Cluster nutzen koennen.
  function renderMarker(k) {
    const istHighlight = highlightKundeId && k.id === highlightKundeId;
    const nachname = k.lastName || k.firstName || '';
    return (
      <Marker
        key={k.id}
        position={[k.lat, k.lng]}
        icon={pinIcon(k.geoQuelle, istHighlight)}
        zIndexOffset={istHighlight ? 1000 : 0}
        ref={(ref) => { if (ref) markerRefs.current[k.id] = ref; }}
      >
        {labelsAnzeigen && nachname && (
          <Tooltip
            permanent
            direction="right"
            offset={[istHighlight ? 12 : 8, -10]}
            className={istHighlight ? 'kk-label kk-label-highlight' : 'kk-label'}
          >
            {nachname}
          </Tooltip>
        )}
        <Popup>
          <div style={{ minWidth: '180px' }}>
            <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>
              <a
                href={`?kunde=${k.id}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: '#9D174D', textDecoration: 'underline' }}
                title="Akte in neuem Tab oeffnen"
              >
                {[k.lastName, k.firstName].filter(Boolean).join(', ') || 'Unbenannt'}
              </a>
              {istHighlight && (
                <span style={{ marginLeft: '6px', fontSize: '10px', color: '#EC4899', fontWeight: 700, textTransform: 'uppercase' }}>
                  aktuell
                </span>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#555', marginBottom: '6px' }}>
              {k.street && <>{k.street}<br/></>}
              {k.zip} {k.city}
            </div>
            <div style={{ fontSize: '12px', marginBottom: '8px' }}>
              Pflegegrad: <strong>{k.careLevel || '–'}</strong>
              {k.tour && <> · Tour: {k.tour}</>}
            </div>
            {onOpenKunde && (
              <button
                onClick={() => onOpenKunde(k.id)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  background: '#9D174D',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: onAddZuTour ? '8px' : 0,
                }}
              >
                Akte oeffnen
              </button>
            )}
            {onAddZuTour && (
              <div style={{ borderTop: '1px solid #eee', paddingTop: '6px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#777', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Zu Tour hinzufuegen
                </div>
                {lokaleTouren.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', maxHeight: '120px', overflowY: 'auto', marginBottom: '6px' }}>
                    {lokaleTouren.slice(0, 8).map(t => (
                      <button
                        key={t.id}
                        onClick={() => onAddZuTour(k.id, t.id)}
                        style={{
                          textAlign: 'left',
                          padding: '5px 8px',
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '4px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                        title={`Hinzufuegen zur Tour „${t.name}" am ${t.datum}`}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                          {t.name}
                        </span>
                        <span style={{ color: '#9D174D', fontWeight: 700, flexShrink: 0 }}>
                          + {t.datum}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {lokaleTouren.length > 8 && (
                  <div style={{ fontSize: '10px', color: '#888', marginTop: '3px', marginBottom: '4px', fontStyle: 'italic' }}>
                    … und {lokaleTouren.length - 8} weitere im Tourplan
                  </div>
                )}

                {/* Neue Tour direkt anlegen — eigene Sub-Komponente mit
                    isoliertem State, damit das Input beim Tippen
                    nicht den Fokus verliert. */}
                <NeueTourPopupForm
                  kundeId={k.id}
                  kundeName={[k.lastName, k.firstName].filter(Boolean).join(', ') || 'Unbenannt'}
                  onAngelegt={handleTourAngelegt}
                />
              </div>
            )}
          </div>
        </Popup>
      </Marker>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-5 pt-4 pb-2 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#9D174D' }}>
            Beratungskunden auf der Karte
          </h2>
          <div className="text-xs text-gray-500 mt-0.5">
            {punkte.length} Pins
            {ohneKoords > 0 && (
              <span className="text-gray-400"> · {ohneKoords} ohne Koordinaten ausgeblendet</span>
            )}
            <span className="text-gray-400"> · grau = nur PLZ-Bereich</span>
            {highlightKundeId && <span style={{ color: '#EC4899' }}> · rosa = aktueller Kunde</span>}
          </div>
        </div>
        <form onSubmit={handleSubmitSuche} className="relative md:w-72">
          <input
            type="text"
            value={suche}
            onChange={(e) => { setSuche(e.target.value); setOrtFehler(null); }}
            placeholder="Kunde oder Ort suchen…"
            className="w-full px-3 py-1.5 pr-20 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
          />
          <button
            type="submit"
            disabled={ortLoading || (suche || '').trim().length < 2}
            className="absolute right-1 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs font-bold rounded text-white disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#9D174D' }}
            title="Enter / Klick: passenden Kunden zoomen, sonst als Ort-Suche an OpenStreetMap"
          >
            {ortLoading ? '…' : (treffer.length > 0 ? 'Kunde' : 'Ort')}
          </button>
          {(treffer.length > 0 || ortFehler) && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-[1000] overflow-hidden">
              {treffer.map(k => (
                <button
                  type="button"
                  key={k.id}
                  onClick={() => springeZuKunde(k)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-fuchsia-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-semibold text-gray-800">
                    {[k.lastName, k.firstName].filter(Boolean).join(', ') || 'Unbenannt'}
                  </div>
                  <div className="text-xs text-gray-500">{k.zip} {k.city}</div>
                </button>
              ))}
              {ortFehler && (
                <div className="px-3 py-2 text-xs text-red-700 bg-red-50 italic">{ortFehler}</div>
              )}
            </div>
          )}
        </form>
      </div>

      <div style={{ height: '480px', width: '100%' }}>
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapRefBridge mapRefOut={mapRef} />
          {/* Ohne Zentrums-Kunden (Home, Tourplan) zoomen wir wie bisher auf
              alle Pins. Mit Zentrums-Kunden (Umkreis-Modal) bleibt die Karte
              eng auf ihm — dann kein FitBounds. */}
          {!hatZentrum && <FitBoundsAufKunden punkte={punkte} />}
          {/* Der aktuell ausgewaehlte Kunde wird separat gerendert, damit er
              nie in einem Cluster verschwindet — immer rosa + Namensschild. */}
          {highlightKunde && renderMarker(highlightKunde)}
          <MarkerClusterGroup chunkedLoading maxClusterRadius={50}>
            {punkte
              .filter(k => !highlightKunde || k.id !== highlightKunde.id)
              .map(k => renderMarker(k))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
    </div>
  );
}
